import React, { useEffect, useState } from 'react';
import { always, cond, equals, filter, propEq } from 'ramda';
import { FormattedMessage } from 'react-intl';
import { useEffectOnce } from 'react-use';
import { AnimatePresence } from 'framer-motion';

import { COLORS } from '@theme/colors';
import { Artist } from '@definitions/artists.types';
import { ArtistList } from '@components/artistList';
import { useMonogram } from '@hooks/useMonogram';
import { renderWhenTrue } from '@utils/rendering';
import { LAYOUT_VARIANT, LAYOUT_VARIANT_ORDER } from '@modules/artists';
import { MONOGRAM_APPEARANCE_TYPE } from '@components/monogram/monogram.constants';
import { ANIMATION_DURATION, ARTIST_POSITION_TYPE } from '@components/PLP/artistView/artistView.constants';

import {
  AllArtists,
  AnimateContainer,
  ArtistsCopy,
  Container,
  CurrentArtists,
  MotionContainer,
  PreviewBio,
  PreviewImage,
  PreviewPanel,
} from './artistsView.styles';

export interface ArtistsViewProps {
  artists: Artist[];
  layoutVariant: LAYOUT_VARIANT;
}

interface PreviewInitialStateType {
  image: string | null;
  bio: string | null;
}

const previewInitialState: PreviewInitialStateType = {
  image: null,
  bio: null,
};

export const ArtistsView = ({ artists, layoutVariant }: ArtistsViewProps) => {
  const featuredArtists = filter(propEq('featured', true), artists);
  const { addMonogram, clearMonogram } = useMonogram();
  const [newLayoutVariant, setNewLayoutVariant] = useState<LAYOUT_VARIANT | null>(null);
  const [oldLayoutVariant, setOldLayoutVariant] = useState<LAYOUT_VARIANT | null>(null);
  const [position, setPosition] = useState<ARTIST_POSITION_TYPE>(ARTIST_POSITION_TYPE.RELATIVE);
  const [preview, setPreview] = useState(previewInitialState);
  const isPreviewVisible = Object.values(preview).some((i) => i !== null);

  useEffectOnce(() => {
    const id = new Date().getTime();

    addMonogram({
      letter: 'A',
      color: COLORS.MAROON_FLUSH,
      appearance: MONOGRAM_APPEARANCE_TYPE.DISAPPEAR,
      id,
    });

    return () => {
      clearMonogram(id);
    };
  });

  const handleOnArtistMouseOver = (artist: Artist) => {
    setPreview({
      image: artist.image?.urls[0].url,
      bio: artist.bio[0].value.paragraph,
    });
  };

  const handleOnArtistMouseOut = () => {
    setPreview(previewInitialState);
  };

  useEffect(() => {
    setTimeout(() => {
      setNewLayoutVariant(layoutVariant);
    });
  }, [layoutVariant]);

  const isReverse =
    layoutVariant && oldLayoutVariant && LAYOUT_VARIANT_ORDER[layoutVariant] < LAYOUT_VARIANT_ORDER[oldLayoutVariant];
  const motionProps = {
    initial: oldLayoutVariant
      ? {
          scale: isReverse ? 2 : 0.5,
          opacity: 0,
        }
      : false,
    animate: {
      scale: 1,
      opacity: 1,
    },
    exit: {
      scale: isReverse ? 0.5 : 2,
      opacity: 0,
    },
    transition: { ease: 'linear', duration: ANIMATION_DURATION / 1000 },
    onAnimationComplete: () => {
      setPosition(ARTIST_POSITION_TYPE.RELATIVE);
      setOldLayoutVariant(layoutVariant);
    },
    onAnimationStart: () => {
      setPosition(ARTIST_POSITION_TYPE.ABSOLUTE);
    },
  };

  const renderLayoutVariant: (...rest: any) => any = cond([
    [
      equals(LAYOUT_VARIANT.GRID),
      always(
        <MotionContainer key={LAYOUT_VARIANT.GRID} {...motionProps} position={position}>
          <ArtistList artists={featuredArtists} isFeatured />
        </MotionContainer>
      ),
    ],
    [
      equals(LAYOUT_VARIANT.LIST),
      always(
        <MotionContainer key={LAYOUT_VARIANT.LIST} {...motionProps} position={position}>
          <ArtistList
            onArtistMouseOver={handleOnArtistMouseOver}
            onArtistMouseOut={handleOnArtistMouseOut}
            isList
            artists={featuredArtists}
            isFeatured
          />
        </MotionContainer>
      ),
    ],
  ]);

  const renderPreview = renderWhenTrue(
    always(
      <PreviewPanel>
        {preview.image && <PreviewImage src={preview.image} />}
        {preview.bio && <PreviewBio dangerouslySetInnerHTML={{ __html: preview.bio }} />}
      </PreviewPanel>
    )
  );

  return (
    <Container>
      <CurrentArtists>
        <ArtistsCopy>
          <FormattedMessage id="artistsView.currentArtists" defaultMessage="Current:" />
        </ArtistsCopy>
      </CurrentArtists>

      <AnimateContainer>
        <AnimatePresence>{renderLayoutVariant(newLayoutVariant)}</AnimatePresence>
      </AnimateContainer>

      <AllArtists>
        <ArtistsCopy>
          <FormattedMessage id="artistsView.allArtists" defaultMessage="All:" />
        </ArtistsCopy>
      </AllArtists>

      <ArtistList
        onArtistMouseOver={handleOnArtistMouseOver}
        onArtistMouseOut={handleOnArtistMouseOut}
        isList
        artists={artists}
      />

      {renderPreview(isPreviewVisible)}
    </Container>
  );
};
